import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  ClipboardList,
  LayoutGrid,
  ChevronRight,
  Loader2,
  AlertTriangle,
  MapPin,
  Hammer,
  Plus,
  Mic,
  Trash2,
  User,
  Phone,
  Mail,
  Shield,
  Calendar,
  Hash,
  Pencil,
  Droplets,
  FileBadge,
} from 'lucide-react';

import useProjectStore from '../stores/projectStore';
import { Project, FieldNote } from '../types';
import { cn, getProjectDisplayName, formatDate } from '../lib/utils';
import { BackendAPI } from '../lib/api';
import { DAMAGE_TYPES, CLAIM_TYPES } from '../data/roomChecklists';

import { Button } from '../components/ui/Button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';

// ================================================================= //
//          Project Detail Page                                        //
// ================================================================= //

type Tab = 'overview' | 'notes' | 'scope';

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Reason: read the single-project error (selectedProjectError), not the
  // list-level `error`, so this page shows its own load failures. See the
  // projectStore field docs (dashboard-shared-error fix).
  const { selectedProject, loading, selectedProjectError: error, getProjectById, setSelectedProject } = useProjectStore();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [isEditOpen, setIsEditOpen] = useState(false);

  useEffect(() => {
    if (id) {
      getProjectById(id);
    }
  }, [id, getProjectById]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4 text-lg text-muted-foreground">Loading Project...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertTriangle className="h-12 w-12" />
        <h2 className="mt-4 text-xl font-semibold">Error Loading Project</h2>
        <p>{error}</p>
        <Button asChild className="mt-4">
          <Link to="/">Go to Dashboard</Link>
        </Button>
      </div>
    );
  }

  if (!selectedProject) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <h2 className="text-xl font-semibold">Project Not Found</h2>
        <Button asChild className="mt-4">
          <Link to="/">Go to Dashboard</Link>
        </Button>
      </div>
    );
  }

  const displayName = getProjectDisplayName(selectedProject);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center text-sm font-medium text-muted-foreground">
        <Link to="/" className="hover:text-primary">Dashboard</Link>
        <ChevronRight className="mx-2 h-4 w-4" />
        <span className="text-foreground truncate max-w-xs">{displayName}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-primary">{displayName}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
            <div className="flex items-center">
              <MapPin className="mr-1.5 h-4 w-4" />
              {selectedProject.address || 'No address'}
            </div>
            {selectedProject.ahj && (
              <div className="flex items-center text-emerald-700">
                <Shield className="mr-1.5 h-4 w-4" />
                AHJ: {selectedProject.ahj}
              </div>
            )}
            {selectedProject.date_of_loss && (
              <div className="flex items-center">
                <Calendar className="mr-1.5 h-4 w-4" />
                Loss: {formatDate(selectedProject.date_of_loss)}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setIsEditOpen(true)}>
            <Pencil className="-ml-1 mr-2 h-4 w-4" />
            Edit Details
          </Button>
          <Button onClick={() => navigate(`/scope/builder?project=${selectedProject.id}`)}>
            <Hammer className="-ml-1 mr-2 h-5 w-5" />
            Generate Scope
          </Button>
        </div>
      </div>

      {isEditOpen && (
        <EditProjectModal
          project={selectedProject}
          onClose={() => setIsEditOpen(false)}
          onSaved={(updated) => {
            setSelectedProject(updated);
            setIsEditOpen(false);
          }}
        />
      )}

      {/* Tabs */}
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && <ProjectOverview project={selectedProject} />}
        {activeTab === 'notes' && <ProjectNotes projectId={selectedProject.id} />}
        {activeTab === 'scope' && <ProjectScopeTab projectId={selectedProject.id} />}
      </div>
    </div>
  );
}

// ================================================================= //
//          Tab Bar                                                     //
// ================================================================= //

const TabBar = ({ activeTab, setActiveTab }: { activeTab: Tab; setActiveTab: (t: Tab) => void }) => {
  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid },
    { id: 'notes', label: 'Notes', icon: FileText },
    { id: 'scope', label: 'Scope', icon: ClipboardList },
  ];

  return (
    <div className="border-b border-border">
      <nav className="-mb-px flex space-x-6" aria-label="Tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'group inline-flex items-center border-b-2 py-3 px-1 text-sm font-medium',
              activeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:border-gray-300 hover:text-foreground'
            )}
          >
            <tab.icon className="-ml-0.5 mr-2 h-5 w-5" />
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
};

// ================================================================= //
//          Overview Tab                                                //
// ================================================================= //

const ProjectOverview = ({ project }: { project: Project }) => {
  const damageLabel = DAMAGE_TYPES.find((d) => d.value === project.damage_type)?.label ?? project.damage_type ?? null;
  const claimLabel = CLAIM_TYPES.find((c) => c.value === project.claim_type)?.label ?? project.claim_type ?? null;

  const fields: { label: string; value: string | null | undefined; icon: React.ElementType }[] = [
    { label: 'Street', value: project.street, icon: MapPin },
    { label: 'City', value: project.city, icon: MapPin },
    { label: 'State', value: project.state, icon: MapPin },
    { label: 'ZIP', value: project.zip, icon: Hash },
    { label: 'Owner Name', value: project.owner_name, icon: User },
    { label: 'Owner Phone', value: project.owner_phone, icon: Phone },
    { label: 'Owner Email', value: project.owner_email, icon: Mail },
    { label: 'Claim Number', value: project.claim_number, icon: Hash },
    { label: 'Policy Number', value: project.policy_number, icon: Hash },
    { label: 'Damage Type', value: damageLabel, icon: Droplets },
    { label: 'Claim Type', value: claimLabel, icon: FileBadge },
    { label: 'Loss Type', value: project.loss_type, icon: FileBadge },
    { label: 'Policy Type', value: project.policy_type, icon: FileBadge },
    { label: 'Date of Loss', value: project.date_of_loss ? formatDate(project.date_of_loss) : null, icon: Calendar },
    { label: 'Date of Inspection', value: project.date_of_inspection ? formatDate(project.date_of_inspection) : null, icon: Calendar },
    { label: 'Authority Having Jurisdiction', value: project.ahj, icon: Shield },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Details</CardTitle>
        <CardDescription>Property, owner, and claim information.</CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {fields.map((f) => (
            <div key={f.label}>
              <dt className="flex items-center text-sm font-medium text-muted-foreground">
                <f.icon className="mr-1.5 h-3.5 w-3.5" />
                {f.label}
              </dt>
              <dd className="mt-1 text-sm text-foreground">{f.value || 'N/A'}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Notes Tab                                                   //
// ================================================================= //

const ProjectNotes = ({ projectId }: { projectId: string }) => {
  const [notes, setNotes] = useState<FieldNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const data = await BackendAPI.getNotesForProject(projectId);
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [projectId]);

  const handleCreateNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNoteContent.trim()) return;

    setIsCreating(true);
    try {
      await BackendAPI.createNote(projectId, {
        content: newNoteContent,
        type: 'text',
      });
      setNewNoteContent('');
      await fetchNotes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create note');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await BackendAPI.deleteNote(projectId, noteId);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete note');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
          <p className="mt-2 text-muted-foreground">Loading notes...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Note Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Add Note</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleCreateNote} className="flex gap-3">
            <Input
              placeholder="Type a field note..."
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={!newNoteContent.trim()} isLoading={isCreating}>
              <Plus className="-ml-1 mr-1 h-4 w-4" />
              Add
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Notes List */}
      <Card>
        <CardHeader>
          <CardTitle>Field Notes</CardTitle>
          <CardDescription>{notes.length} note{notes.length !== 1 ? 's' : ''} recorded.</CardDescription>
        </CardHeader>
        <CardContent>
          {notes.length > 0 ? (
            <ul className="space-y-3">
              {notes.map((note) => (
                <li key={note.id} className="group flex items-start gap-3 rounded-md border p-3">
                  <NoteTypeIcon type={note.type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">{note.content}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {note.created_at ? formatDate(note.created_at) : note.time}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteNote(note.id)}
                    className="opacity-0 group-hover:opacity-100 rounded p-1 text-muted-foreground hover:text-destructive transition-opacity"
                    title="Delete note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              No notes yet. Add your first field note above.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const NoteTypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'voice':
      return <Mic className="h-4 w-4 mt-0.5 text-blue-500 flex-shrink-0" />;
    case 'photo':
      return <FileText className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />;
    default:
      return <FileText className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />;
  }
};

// ================================================================= //
//          Scope Tab                                                   //
// ================================================================= //

const ProjectScopeTab = ({ projectId }: { projectId: string }) => {
  const navigate = useNavigate();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Scope of Work</CardTitle>
        <CardDescription>Generate an AI-powered scope from this project's field notes.</CardDescription>
      </CardHeader>
      <CardContent className="text-center py-12">
        <Hammer className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">
          Click below to generate a scope using this project's notes and building code data.
        </p>
        <Button
          className="mt-6"
          size="lg"
          onClick={() => navigate(`/scope/builder?project=${projectId}`)}
        >
          <Hammer className="-ml-1 mr-2 h-5 w-5" />
          Generate Scope
        </Button>
      </CardContent>
    </Card>
  );
};

// ================================================================= //
//          Edit Project Modal                                          //
// ================================================================= //

interface EditProjectModalProps {
  project: Project;
  onClose: () => void;
  onSaved: (updated: Project) => void;
}

const EditProjectModal = ({ project, onClose, onSaved }: EditProjectModalProps) => {
  const [form, setForm] = useState({
    street: project.street ?? '',
    city: project.city ?? '',
    state: project.state ?? '',
    zip: project.zip ?? '',
    owner_name: project.owner_name ?? '',
    owner_phone: project.owner_phone ?? '',
    owner_email: project.owner_email ?? '',
    claim_number: project.claim_number ?? '',
    policy_number: project.policy_number ?? '',
    date_of_loss: project.date_of_loss ?? '',
    date_of_inspection: project.date_of_inspection ?? '',
    damage_type: project.damage_type ?? '',
    claim_type: project.claim_type ?? '',
    loss_type: project.loss_type ?? '',
    policy_type: project.policy_type ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    // Reason: send only fields the user actually filled, so we don't
    // overwrite existing values with empty strings.
    const payload: Partial<Project> = {};
    for (const [k, v] of Object.entries(form)) {
      if (typeof v === 'string' && v.trim().length > 0) {
        (payload as Record<string, string>)[k] = v.trim();
      }
    }
    try {
      const updated = await BackendAPI.updateProject(project.id, payload);
      onSaved(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <Card className="w-full max-w-2xl border border-slate-200 shadow-xl bg-white mx-4 max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <CardHeader>
            <CardTitle>Edit Project</CardTitle>
            <CardDescription>Update property, owner, and claim details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-street">Street</Label>
              <Input
                id="edit-street"
                value={form.street}
                onChange={(e) => setForm((p) => ({ ...p, street: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="edit-city">City</Label>
                <Input
                  id="edit-city"
                  value={form.city}
                  onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-state">State</Label>
                <Input
                  id="edit-state"
                  maxLength={2}
                  value={form.state}
                  onChange={(e) => setForm((p) => ({ ...p, state: e.target.value.toUpperCase() }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-zip">ZIP</Label>
                <Input
                  id="edit-zip"
                  value={form.zip}
                  onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-owner-name">Owner Name</Label>
                <Input
                  id="edit-owner-name"
                  value={form.owner_name}
                  onChange={(e) => setForm((p) => ({ ...p, owner_name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-owner-phone">Owner Phone</Label>
                <Input
                  id="edit-owner-phone"
                  value={form.owner_phone}
                  onChange={(e) => setForm((p) => ({ ...p, owner_phone: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-owner-email">Owner Email</Label>
              <Input
                id="edit-owner-email"
                type="email"
                value={form.owner_email}
                onChange={(e) => setForm((p) => ({ ...p, owner_email: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-claim-number">Claim Number</Label>
                <Input
                  id="edit-claim-number"
                  value={form.claim_number}
                  onChange={(e) => setForm((p) => ({ ...p, claim_number: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-policy-number">Policy Number</Label>
                <Input
                  id="edit-policy-number"
                  value={form.policy_number}
                  onChange={(e) => setForm((p) => ({ ...p, policy_number: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-damage-type">Damage Type</Label>
                <select
                  id="edit-damage-type"
                  value={form.damage_type}
                  onChange={(e) => setForm((p) => ({ ...p, damage_type: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select damage type...</option>
                  {DAMAGE_TYPES.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-claim-type">Claim Type</Label>
                <select
                  id="edit-claim-type"
                  value={form.claim_type}
                  onChange={(e) => setForm((p) => ({ ...p, claim_type: e.target.value }))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select claim type...</option>
                  {CLAIM_TYPES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-loss-type">Loss Type</Label>
                <Input
                  id="edit-loss-type"
                  placeholder="e.g. burst pipe, roof leak"
                  value={form.loss_type}
                  onChange={(e) => setForm((p) => ({ ...p, loss_type: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-policy-type">Policy Type</Label>
                <Input
                  id="edit-policy-type"
                  placeholder="e.g. HO-3, DP-3"
                  value={form.policy_type}
                  onChange={(e) => setForm((p) => ({ ...p, policy_type: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-date-loss">Date of Loss</Label>
                <Input
                  id="edit-date-loss"
                  type="date"
                  value={form.date_of_loss}
                  onChange={(e) => setForm((p) => ({ ...p, date_of_loss: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-date-inspection">Date of Inspection</Label>
                <Input
                  id="edit-date-inspection"
                  type="date"
                  value={form.date_of_inspection}
                  onChange={(e) => setForm((p) => ({ ...p, date_of_inspection: e.target.value }))}
                />
              </div>
            </div>
            {error && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {error}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-end space-x-2">
            <Button variant="ghost" type="button" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" isLoading={saving}>
              Save Changes
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};
